import React, { useRef } from 'react';

import type { UUID } from '@aline/common';
import type { OnSubmitCommentFn, OnSubmitCommentReturn } from 'src/components';
import { makeStyles } from 'tss-react/mui';

import type { ActivityItemFragment } from './activity-types';
import { ActivityComposer } from './ActivityComposer';
import type { ActivityFeedHandle } from './ActivityFeed';
import { ActivityFeed } from './ActivityFeed';
import type { ActivityNavigation } from './use-activity-navigation';
import { useScrollFeedOnSubmit } from './use-scroll-feed-on-submit';
import type { OnSubmitAiFn } from './use-start-ai-thread';

const useStyles = makeStyles()(theme => ({
  composerDock: {
    backgroundColor: theme.palette.common.white,
    flexShrink: 0,
    paddingTop: theme.spacing(1),
  },
  root: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minHeight: 0,
    overflow: 'hidden',
  },
}));

interface ActivitySectionProps {
  items: ActivityItemFragment[];
  navigation: ActivityNavigation;
  onSubmitComment: OnSubmitCommentFn;
  onViewThread: (threadId: UUID) => void;
  workflowId: UUID;
  fetchMoreActivity?: () => Promise<unknown>;
  fetchPreviousActivity?: () => Promise<unknown>;
  hasMore?: boolean;
  hasPrevious?: boolean;
  loadedAnchor?: UUID | null;
  loading?: boolean;
  loadingMore?: boolean;
  loadingPrevious?: boolean;
  onOpenEmailThread?: (activityItemId: UUID) => void;
  onSubmitAI?: OnSubmitAiFn;
  onSubmitReply?: (parentCommentId: UUID, markdown: string) => Promise<OnSubmitCommentReturn>;
  placeholder?: string;
  showDayHeadings?: boolean;
  showHeader?: boolean;
}

/** Activity feed with a composer pinned below its scrollable history. */
export function ActivitySection({
  fetchMoreActivity,
  fetchPreviousActivity,
  hasMore,
  hasPrevious,
  items,
  loadedAnchor,
  loading,
  loadingMore,
  loadingPrevious,
  navigation,
  onOpenEmailThread,
  onSubmitAI,
  onSubmitComment,
  onSubmitReply,
  onViewThread,
  placeholder = 'Add a comment, type @ to mention someone…',
  showDayHeadings,
  showHeader,
  workflowId,
}: ActivitySectionProps): JSX.Element {
  const { classes } = useStyles();
  const feedRef = useRef<ActivityFeedHandle | null>(null);
  const { handleSubmitAI, handleSubmitComment } = useScrollFeedOnSubmit(feedRef, {
    onSubmitAI,
    onSubmitComment,
  });

  /**
   * Namespace the composer's editor-registry key by workflow id so a draft can't leak across
   * workflow activity surfaces.
   */
  const composerId = `workflow-composer:${workflowId}`;

  return (
    <div className={classes.root}>
      <ActivityFeed
        ref={feedRef}
        fetchMoreActivity={fetchMoreActivity}
        fetchPreviousActivity={fetchPreviousActivity}
        hasMore={hasMore}
        hasPrevious={hasPrevious}
        items={items}
        loadedAnchor={loadedAnchor}
        loading={loading}
        loadingMore={loadingMore}
        loadingPrevious={loadingPrevious}
        navigation={navigation}
        showDayHeadings={showDayHeadings}
        showHeader={showHeader}
        workflowId={workflowId}
        onOpenEmailThread={onOpenEmailThread}
        onSubmitReply={onSubmitReply}
        onViewThread={onViewThread}
      />
      <div className={classes.composerDock}>
        <ActivityComposer
          // Remount when the mention entity changes so a draft can't leak across entities.
          key={composerId}
          editorId={composerId}
          placeholder={placeholder}
          workflowId={workflowId}
          onSubmit={handleSubmitComment}
          onSubmitAI={handleSubmitAI}
        />
      </div>
    </div>
  );
}
